// The app's state. Server data (boards, items, streams...) plus the few UI
// facts several components need. Components subscribe to narrow slices.

import { create } from "zustand";
import type { FileCopy, Activity, ActivityTo, AgentState, ApiKey, Board, ButtonRun, Connection, CustomTheme, Entry, ID, InkShape, Item, Limits, Pull, ServiceKey, Snapshot, Stream, Usage, WidgetType, Workspace, RunnerState, RunnerTurn } from "../../shared/types";
import { setMadeThemes } from "../themes/themes";
import type { ThemeShare } from "./themes";
import type { RenderReport } from "../../shared/widgetKit";
import { phoneSized } from "../phone/size";
import { leftOff, linkedBoard } from "../help/leftOff";
import { shownRunLine } from "../../shared/runLine";
import { readStored, writeStored } from "../lib/stored";
import { limitLine } from "./limits";

export type Tool = "select" | "pen" | "rect" | "ellipse" | "arrow" | "text" | "eraser";

export type Panel = null | "gallery" | "theme" | "connect" | "data" | "share" | "settings" | "shortcuts" | "source" | "boards" | "newboard" | "connections" | "instructions" | "import" | "help";

export interface Toast {
  id: string;
  title: string;
  body?: string | null;
  level: "info" | "success" | "warn" | "error";
  who?: string | null;
  /** the app an agent came through, when it named itself otherwise: its mark (Fixes 9) */
  via?: string | null;
  action?: { label: string; run: () => void } | null;
  itemId?: string | null;
  /** where clicking it goes, as a line of Board activity does (Features 1, 20 Oct) */
  to?: ActivityTo | null;
  /** the widgets a run of an agent's changes was about, newest last */
  itemIds?: string[];
  /** how many changes this one stands for */
  count?: number;
  /** a newer toast with the same key takes its place */
  key?: string;
  /** what the sender wants to keep with it, such as the look an undo goes back to */
  meta?: unknown;
  t: number;
  sticky?: boolean;
}

export interface InkPrefs {
  color: string;
  size: number;
  opacity: number;
  fill: string | null;
  dash: boolean;
  font: Extract<InkShape, { t: "text" }>["font"];
  fontSize: number;
}

export interface AppState {
  /** shared: someone with no board of their own, signed in to see what was shared with them (Features 1, sharing) */
  phase: "boot" | "welcome" | "ready" | "error" | "shared";
  /** a board shared with this person, open on this page with a pass (state/guest.ts); null on one's own boards */
  guest: import("./guest").GuestState | null;
  /** boards others shared with this person, for the boards list */
  shared: import("../../shared/sharing").SharedBoard[];
  bootError: string | null;
  bootErrorCode?: string | null;
  clientId: string;
  workspace: Workspace | null;
  /** the boards in use, in the list's order: every list, menu and command reads these */
  boards: Board[];
  /** the archived ones, newest first (Features 1, 20 Oct): only Archived shows them, and one opens read only */
  archived: Board[];
  /** the bell's notices, newest first, and the reminders still to come (Features 1, 20 Oct) */
  notices: import("../../shared/notify").Notice[];
  reminders: import("../../shared/notify").Reminder[];
  items: Record<ID, Item>;
  /** what typed boards hold of their own (a doc board's pages), by id */
  entries: Record<ID, Entry>;
  streams: Record<string, Stream>;
  pulls: Record<ID, Pull>;
  connections: Record<ID, Connection>;
  /** the services whose key the user saved, by service id: never the key */
  services: Record<string, ServiceKey>;
  /** themes this workspace made; the palettes live with the code */
  themes: Record<string, CustomTheme>;
  /** a theme shown on the board while someone looks at it, not yet worn */
  themePreview: string | null;
  /** the strip of themes on ⇧T */
  themePicker: boolean;
  /** a theme someone shared by link, waiting to be added */
  sharedTheme: ThemeShare | null;
  activity: Activity[];
  agents: Record<string, AgentState>;
  keys: ApiKey[];
  /** the user's runner: their own computer, running the chat on their Claude Code or Codex */
  runner: RunnerState | null;
  /** chat turns the runner is still working on, which a page opened in the middle of one follows */
  runnerTurns: RunnerTurn[];
  /** the briefs and jobs running now on the user's computer, by widget id, so their widgets say so */
  jobsRunning: Record<ID, number>;
  /** each Button's last run, by widget id, as the board keeps it for every device of the owner's (Round 7) */
  buttonRuns: Record<ID, ButtonRun>;
  /** the project files the board's Project file widgets show, by path, as the user's computer last sent them */
  files: Record<string, FileCopy>;
  limits: Limits | null;
  /** what the account has used of them, as the board last said */
  usage: Usage | null;
  serverOffset: number;
  /** a shared or public view: whether the board's owner has X Premium (their setting, which the view's own account lacks) */
  ownerXPremium: boolean | null;
  /** the signature of the board's tools, from its hello: the chat lists them again when it changes (worker/mcp.ts toolsSig) */
  toolsSig: string | null;
  lastVisitAt: number | null;

  boardId: ID | null;
  selection: ID[];
  editing: ID | null;
  tool: Tool;
  ink: InkPrefs;
  panel: Panel;
  panelArg: unknown;
  save: "saved" | "saving" | "error" | "offline";
  saveError: string | null;
  live: "connecting" | "live" | "offline";
  toasts: Toast[];
  glow: Record<ID, number>;
  focusMode: boolean;
  /** full screen: the browser's whole screen, no controls, the board fitted to it */
  presenting: boolean;
  /** in full screen, only these widgets show; null shows the whole board */
  presentIds: ID[] | null;
  /** a colour tried on widgets while its swatch is under the pointer, not saved */
  tintPreview: { ids: ID[]; color: string } | null;
  /** the custom widget whose code is open in the editor */
  codeFor: ID | null;
  /**
   * The widget's settings are open beside it. A click on a widget selects it and leaves it to be used; a
   * double-click, or Settings in its menu, opens them, and they follow the selection until it is empty.
   */
  inspect: boolean;
  /** a request on its way to the user's own agent, shown once it is connected */
  pendingAsk: { agent: string } | null;
  /** how each custom widget drew last, as its frame reported it */
  renders: Record<ID, { hash: string; report: RenderReport; at: number }>;
  mobile: boolean;
}

const clientId = "c" + Math.random().toString(36).slice(2, 10);

export const useStore = create<AppState>(() => ({
  phase: "boot",
  guest: null,
  shared: [],
  bootError: null,
  clientId,
  workspace: null,
  boards: [],
  archived: [],
  notices: [],
  reminders: [],
  items: {},
  entries: {},
  streams: {},
  pulls: {},
  connections: {},
  services: {},
  themes: {},
  themePreview: null,
  themePicker: false,
  sharedTheme: null,
  activity: [],
  agents: {},
  keys: [],
  runner: null,
  runnerTurns: [],
  jobsRunning: {},
  buttonRuns: {},
  files: {},
  limits: null,
  usage: null,
  serverOffset: 0,
  ownerXPremium: null,
  toolsSig: null,
  lastVisitAt: null,

  boardId: null,
  selection: [],
  editing: null,
  tool: "select",
  ink: { color: "auto", size: 3, opacity: 1, fill: null, dash: false, font: "hand", fontSize: 28 },
  panel: null,
  panelArg: null,
  save: "saved",
  saveError: null,
  live: "connecting",
  toasts: [],
  glow: {},
  focusMode: false,
  presenting: false,
  presentIds: null,
  tintPreview: null,
  renders: {},
  pendingAsk: null,
  codeFor: null,
  inspect: false,
  mobile: phoneSized(),
}));

export const get = () => useStore.getState();

// ---------------------------------------------------------------------------
// words not yet saved

/** A dialog or panel that can hold words someone typed in it (ui.tsx's Typed). */
export interface Holder {
  /** whether it holds words typed there and not saved */
  holds(): boolean;
  /** whether the user is acting in it with this event: its own Close, Cancel, Save or link, or Enter in one of its fields */
  acting(e: Event): boolean;
  /** shows that it stayed when something else asked for its place */
  nudge(): void;
}
const holders = new Set<Holder>();

/** Counts what a holder holds, until the function it returns is called. */
export function watchHolder(h: Holder): () => void {
  holders.add(h);
  return () => void holders.delete(h);
}

/** The dialog or panel open now that holds words someone typed there and has not saved, if one does. */
export function holding(): Holder | null {
  for (const h of holders) if (h.holds()) return h;
  return null;
}

/** What the board holds, as against what is only on the screen: a change to it is a save. */
const DATA = ["workspace", "boards", "items", "entries", "streams", "pulls", "connections", "services", "themes", "keys"] as const;
let dataVersion = 0;
/** Goes up with every change to what the board holds: a field whose own handler changed it saved what it held. */
export const savedVersion = () => dataVersion;

// The key, click or form the user is acting with, while it is being handled: an event still on its way
// through the page has a phase, and one that has finished has none. Nested ones (a click a handler sends
// on) are kept in order, so the one being handled is the last that has not finished.
const USER_EVENTS = ["keydown", "keyup", "pointerdown", "pointerup", "mousedown", "mouseup", "click", "dblclick", "auxclick", "contextmenu", "submit", "change", "input", "paste", "drop", "touchend"];
let handling: Event[] = [];
if (typeof window !== "undefined" && typeof window.addEventListener === "function")
  for (const type of USER_EVENTS)
    window.addEventListener(
      type,
      (e: Event) => {
        handling = handling.filter((x) => (x.eventPhase ?? 0) !== 0);
        handling.push(e);
      },
      true,
    );
function userEvent(): Event | null {
  for (let k = handling.length - 1; k >= 0; k--) if ((handling[k].eventPhase ?? 0) !== 0) return handling[k];
  return null;
}

/**
 * Who keeps the panel's place from this change, if anyone does. A dialog or panel holding words someone typed
 * there and has not saved is closed or replaced only by the user's own act in it: its Close, Cancel or Save,
 * Esc, Enter in one of its fields, or, with no key or click being handled, its own work finishing (a save
 * that waited for the server closes it). Any other key, button or chat tool leaves it, words and all.
 */
function keepsPlace(prev: AppState, next: Partial<AppState>): Holder | null {
  const moves = ("panel" in next && next.panel !== prev.panel) || ("panelArg" in next && next.panelArg !== prev.panelArg);
  if (!moves) return null;
  const h = holding();
  if (!h) return null;
  const closes = "panel" in next && next.panel === null;
  const e = userEvent();
  if (!e) return closes ? null : h;
  if (closes && e.type === "keydown" && (e as KeyboardEvent).key === "Escape") return null;
  return h.acting(e) ? null : h;
}

/** A change to the app's state: some of it, or a function of what it is now. */
type Change = AppState | Partial<AppState> | ((s: AppState) => AppState | Partial<AppState>);

/** Makes a change unless the guard keeps a dialog's place from it, and says whether it was made. */
function apply(change: Change, replace?: boolean): boolean {
  const prev = useStore.getState();
  const next = typeof change === "function" ? change(prev) : change;
  const keeper = keepsPlace(prev, next);
  if (keeper) {
    keeper.nudge();
    return false;
  }
  for (const k of DATA) if (k in next && next[k] !== prev[k]) dataVersion++;
  if (replace) useStore.setState(next as AppState, true);
  else useStore.setState(next);
  return true;
}

/**
 * Every change to the app's state goes through here, and one guard sits in it: a dialog or panel holding
 * words someone typed there and has not saved keeps its place (keepsPlace). A change that would close or
 * replace it is not made at all, and the dialog shows it stayed. set.made does the same and says whether
 * the change was made, for a caller that must say what happened (the chat's tools).
 */
export const set = Object.assign((change: Change, replace?: boolean): void => void apply(change, replace), { made: (change: Change): boolean => apply(change) });

/** Whether the code editor can show: for a custom widget that is there, on a screen wide enough for it (a phone's column has no editor). */
export function codeShows(s: AppState): boolean {
  const w = s.codeFor ? s.items[s.codeFor] : undefined;
  return !s.mobile && !!w && w.kind === "widget" && w.type === "custom";
}

/** Whether a theme someone shared can show, waiting to be added: on a screen wide enough for its dialog. */
export function sharedShows(s: AppState): boolean {
  return !s.mobile && !!s.sharedTheme;
}

// The code editor lets go of a widget that is gone (an agent removed it, or another tab, or an undo)
// and of a window narrowed to a phone's width, which has no editor. Then nothing waits on it (all
// boards would never open), and the editor never opens again by itself when its widget comes back.
// A board that is reloaded whole (sync.ts resync) keeps a widget still being saved in the same step,
// so the editor is never closed by a widget missing for a moment.
//
// A theme someone shared waits through a narrow window instead: its link has left the address, so
// letting go would lose it. Its dialog shows again once the window is wide, and while it cannot
// show, nothing waits on it (sharedShows is false).
useStore.subscribe((s) => {
  if (s.codeFor && !codeShows(s)) set({ codeFor: null });
  // nothing selected, nothing to show the settings of: the next click on a widget leaves them shut
  if (s.inspect && !s.selection.length) set({ inspect: false });
});

/** Where the account left off, picked at a first load, for its page to open once the board draws (src/help/place.ts). */
export let pendingPlace: { board: ID; page: ID | null } | null = null;
export const takePendingPlace = () => {
  const p = pendingPlace;
  pendingPlace = null;
  return p;
};

export function loadSnapshot(s: Snapshot, keepBoard = true) {
  const items: Record<ID, Item> = {};
  for (const i of s.items) items[i.id] = i;
  const entries: Record<ID, Entry> = {};
  for (const e of s.entries ?? []) entries[e.id] = e;
  const streams: Record<string, Stream> = {};
  for (const st of s.streams) streams[st.key] = st;
  const pulls: Record<ID, Pull> = {};
  for (const p of s.pulls) pulls[p.id] = p;
  const agents: Record<string, AgentState> = {};
  for (const a of s.agents) agents[a.name] = a;
  const connections: Record<ID, Connection> = {};
  for (const c of s.connections ?? []) connections[c.id] = c;
  const services: Record<string, ServiceKey> = {};
  for (const k of s.services ?? []) services[k.id] = k;
  const themes: Record<string, CustomTheme> = {};
  for (const t of s.themes ?? []) themes[t.id] = t;
  // before anything renders: a board may wear one of them
  setMadeThemes(s.themes ?? []);
  const { boards, archived } = splitBoards(s.boards);
  const prev = get();
  const remembered = readStored("cb:board");
  // an archived board that was open stays open, read only; a reload opens the one remembered if it is in use, and a
  // first load opens where the account left off on any device when that is newer (Features 4, src/help/place.ts)
  const kept = keepBoard && prev.boardId && [...boards, ...archived].some((b) => b.id === prev.boardId) ? prev.boardId : null;
  const here = remembered && boards.some((b) => b.id === remembered) ? remembered : null;
  // a board's own address, opened, a bookmark or a link sent (Fixes 10): that board, before anything remembered
  // (a load that keeps no board reads it again: the app loads twice as it starts)
  const asked = !kept && !prev.guest ? linkedBoard() : null;
  const linked = asked && [...boards, ...archived].some((b) => b.id === asked) ? asked : null;
  const away = !kept && !linked && !prev.guest ? leftOff(s.workspace.onboarding, boards, here) : null;
  const pick = kept ?? linked ?? away?.board ?? here ?? boards[0]?.id ?? null;
  if (away) pendingPlace = away;
  set({
    workspace: s.workspace,
    boards,
    archived,
    notices: s.notices ?? [],
    reminders: s.reminders ?? [],
    items,
    entries,
    streams,
    pulls,
    connections,
    services,
    themes,
    // a hands-free run's line from before Fixes 8 reads as the lines are written now
    activity: s.activity.map(shownRunLine),
    agents,
    keys: s.keys,
    runner: s.runner ?? null,
    runnerTurns: s.runnerTurns ?? [],
    jobsRunning: s.jobsRunning ?? {},
    buttonRuns: s.buttonRuns ?? {},
    files: Object.fromEntries((s.files ?? []).map((f) => [f.path, f])),
    limits: s.limits,
    usage: s.usage ?? null,
    serverOffset: s.serverTime - Date.now(),
    boardId: pick,
    lastVisitAt: prev.phase === "ready" ? prev.lastVisitAt : s.workspace.lastVisitAt,
  });
  // a limit reached (David, 31 Oct): its one short line, once a day on this device
  const line = limitLine(s.usage, s.limits);
  const day = new Date().toISOString().slice(0, 10);
  if (line && readStored("cb:limit-said") !== day) {
    writeStored("cb:limit-said", day);
    toast({ level: "warn", title: line });
  }
}

// ---------------------------------------------------------------------------
// selectors

export function boardItems(state: AppState, boardId: ID | null = state.boardId): Item[] {
  if (!boardId) return [];
  const out: Item[] = [];
  for (const id in state.items) {
    const i = state.items[id];
    if (i.boardId === boardId) out.push(i);
  }
  return out.sort((a, b) => a.z - b.z);
}

/**
 * The widgets on a board as a list of them names them, a line each: id, type,
 * title and stream, and never where they sit, so dragging one changes nothing
 * here and a list of widgets (the command bar's) is not drawn again for it.
 * Each line is read back whole by widgetLine, whatever a title holds: an agent
 * can give one a line break.
 */
export function widgetLines(state: AppState, boardId: ID | null = state.boardId): string[] {
  const out: string[] = [];
  for (const i of boardItems(state, boardId)) if (i.kind === "widget") out.push(JSON.stringify([i.id, i.type, i.title ?? "", i.stream ?? ""]));
  return out;
}

/** One line of widgetLines, read back. */
export function widgetLine(line: string): { id: ID; type: WidgetType; title: string; stream: string } {
  const [id, type, title, stream] = JSON.parse(line) as [ID, WidgetType, string, string];
  return { id, type, title, stream };
}

export function currentBoard(state: AppState): Board | null {
  return state.boards.find((b) => b.id === state.boardId) ?? state.archived.find((b) => b.id === state.boardId) ?? null;
}

/** The boards in use, in the list's order, and the archived ones, newest first. */
export function splitBoards(all: Board[]): { boards: Board[]; archived: Board[] } {
  return {
    boards: all.filter((b) => !b.archived).sort((a, b) => a.order - b.order),
    archived: all.filter((b) => b.archived).sort((a, b) => b.archived! - a.archived!),
  };
}

/** A board's own entries (a doc board's pages), in no order: shared/entries.ts puts them in one. */
export function boardEntries(state: AppState, boardId: ID | null = state.boardId): Entry[] {
  if (!boardId) return [];
  const out: Entry[] = [];
  for (const id in state.entries) if (state.entries[id].boardId === boardId) out.push(state.entries[id]);
  return out;
}

export function now(): number {
  return Date.now() + get().serverOffset;
}

// ---------------------------------------------------------------------------
// toasts

let toastSeq = 0;
const toastTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * A notification, bottom right. A burst from the same agent folds into one
 * ("Claude Code made 3 changes"), a toast with a key replaces the one before
 * it, and no more than three show at once: the board stays calm.
 */
export function toast(t: Omit<Toast, "id" | "t"> & { ms?: number }): string {
  const now = Date.now();
  const ms = t.ms ?? (t.level === "error" ? 9000 : t.action ? 7000 : 4500);
  const all = get().toasts;
  const last = all[all.length - 1];
  const agent = t.who && t.who !== "You" && t.who !== "Context Board";
  if (agent && !t.action && !t.key && last && last.who === t.who && !last.action && !last.key && now - last.t < 8000) {
    const count = (last.count ?? 1) + 1;
    const ids = [...(last.itemIds ?? (last.itemId ? [last.itemId] : [])), ...(t.itemId ? [t.itemId] : [])];
    // the body lists the latest three, in the order they happened
    const earlier = last.count ? (last.body ?? "").split(" · ").filter(Boolean) : [last.title];
    const lines = [...earlier, t.title].slice(-3);
    // a click goes where the newest change was, or to every widget the run touched when they share its board
    const merged: Toast = { ...last, title: `Made ${count} changes`, body: lines.join(" · "), itemIds: ids, itemId: ids[ids.length - 1] ?? null, to: t.to !== undefined ? t.to : last.to, count, t: now };
    set((s) => ({ toasts: s.toasts.map((x) => (x.id === last.id ? merged : x)) }));
    clearTimeout(toastTimers.get(last.id));
    toastTimers.set(last.id, setTimeout(() => dismissToast(last.id), Math.max(ms, 6000)));
    return last.id;
  }
  const id = "t" + ++toastSeq;
  const entry: Toast = { ...t, id, t: now };
  set((s) => ({ toasts: [...s.toasts.filter((x) => !t.key || x.key !== t.key).slice(-2), entry] }));
  if (!t.sticky) toastTimers.set(id, setTimeout(() => dismissToast(id), ms));
  return id;
}

export function dismissToast(id: string) {
  clearTimeout(toastTimers.get(id));
  toastTimers.delete(id);
  set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
}

export function glowItems(ids: ID[]) {
  if (!ids.length) return;
  const t = Date.now();
  set((s) => {
    const glow = { ...s.glow };
    for (const id of ids) glow[id] = t;
    return { glow };
  });
}
